import { useEffect, useRef, useState } from 'react';
import {
  PHONE_DIALS,
  confirmPhoneVerification,
  logPhoneSignupFailure,
  mapAuthError,
  requestPhoneOtp,
  resetPhoneRecaptcha,
  shouldOfferEmailSignup,
  toE164,
} from '../../../services/auth';
import { useLocalization } from '../../localization/LocalizationContext';
import { ArrowRight } from 'lucide-react';

/**
 * Shared phone OTP flow for Login and SignUp.
 * reCAPTCHA is handled invisibly inside requestPhoneOtp (auth.js).
 */
const PhoneAuthPanel = ({
  mode = 'login', // 'login' | 'signup'
  onSuccess,
  onUseEmail,
  submitLabel,
}) => {
  const { local } = useLocalization();
  const defaultDial =
    PHONE_DIALS.find((d) => d.code === local)?.dial ||
    PHONE_DIALS.find((d) => d.code === 'NG')?.dial ||
    '+234';

  const [dial, setDial] = useState(defaultDial);
  const [national, setNational] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('phone'); // 'phone' | 'otp'
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [e164, setE164] = useState('');

  const confirmationRef = useRef(null);

  useEffect(() => {
    const next = PHONE_DIALS.find((d) => d.code === local)?.dial;
    if (next) setDial(next);
  }, [local]);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const id = setTimeout(() => setResendIn((n) => n - 1), 1000);
    return () => clearTimeout(id);
  }, [resendIn]);

  useEffect(() => {
    return () => {
      resetPhoneRecaptcha();
      confirmationRef.current = null;
    };
  }, []);

  const handlePhoneError = (err, { step: failureStep, phone } = {}) => {
    if (mode === 'signup') {
      logPhoneSignupFailure(err, { step: failureStep, phone, locale: local });
    }

    if (err?.code === 'auth/missing-display-name') {
      setError('Enter your full name to continue.');
      return;
    }
    setError(mapAuthError(err));
    if (mode === 'signup' && onUseEmail && shouldOfferEmailSignup(err)) {
      onUseEmail({ displayName: displayName.trim(), auto: true });
    }
  };

  const handleSendCode = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (mode === 'signup' && !displayName.trim()) {
        throw Object.assign(new Error('Enter your full name.'), {
          code: 'auth/missing-display-name',
        });
      }

      const phone = toE164(dial, national);
      if (!phone) {
        throw Object.assign(new Error('Enter a valid phone number.'), {
          code: 'auth/invalid-phone-number',
        });
      }

      const confirmation = await requestPhoneOtp(phone);
      confirmationRef.current = confirmation;
      setE164(phone);
      setStep('otp');
      setOtp('');
      setResendIn(30);
    } catch (err) {
      handlePhoneError(err, {
        step: 'send-code',
        phone: toE164(dial, national) || e164,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmCode = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await confirmPhoneVerification(confirmationRef.current, otp, {
        displayName: mode === 'signup' ? displayName : undefined,
      });
      onSuccess?.(result);
    } catch (err) {
      handlePhoneError(err, { step: 'verify-code', phone: e164 });
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendIn > 0 || loading) return;
    setError('');
    setLoading(true);
    try {
      const confirmation = await requestPhoneOtp(e164);
      confirmationRef.current = confirmation;
      setResendIn(30);
    } catch (err) {
      handlePhoneError(err, { step: 'resend-code', phone: e164 });
    } finally {
      setLoading(false);
    }
  };

  const handleChangeNumber = async () => {
    setStep('phone');
    setOtp('');
    setError('');
    confirmationRef.current = null;
    await resetPhoneRecaptcha();
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="border border-red-300 bg-red-50 p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {step === 'phone' ? (
        <form className="space-y-4" onSubmit={handleSendCode}>
          {mode === 'signup' && (
            <div>
              <label
                htmlFor="phone-displayName"
                className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700"
              >
                Full name
              </label>
              <input
                id="phone-displayName"
                name="displayName"
                type="text"
                required
                autoComplete="name"
                className="w-full bg-white px-4 py-3 text-ink-900 placeholder-ink-600/40 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                placeholder="Amara Okonkwo"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </div>
          )}

          <div>
            <label
              htmlFor="phone-national"
              className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700"
            >
              Phone number
            </label>
            <div className="flex gap-2">
              <select
                aria-label="Country dial code"
                value={dial}
                onChange={(e) => setDial(e.target.value)}
                className="max-w-[9.5rem] flex-none bg-white px-3 py-3 text-sm text-ink-900 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
              >
                {PHONE_DIALS.map((item) => (
                  <option key={item.code} value={item.dial}>
                    {item.dial}
                  </option>
                ))}
              </select>
              <input
                id="phone-national"
                name="phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel-national"
                required
                className="min-w-0 flex-1 bg-white px-4 py-3 text-ink-900 placeholder-ink-600/40 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                placeholder="801 234 5678"
                value={national}
                onChange={(e) => setNational(e.target.value)}
              />
            </div>
            <p className="mt-2 text-xs leading-relaxed text-ink-600/70">
              We&apos;ll text you a one-time code. Message and data rates may apply.
            </p>
          </div>

          <button
            id="phone-send-code"
            type="submit"
            disabled={loading}
            className="group flex w-full items-center justify-center gap-2 bg-saffron-500 px-6 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? (
              'Sending code…'
            ) : (
              <>
                {submitLabel || 'Send code'}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </>
            )}
          </button>
        </form>
      ) : (
        <form className="space-y-4" onSubmit={handleConfirmCode}>
          <div>
            <p className="text-sm text-ink-700/80">
              Enter the 6-digit code sent to{' '}
              <span className="font-medium text-ink-900">{e164}</span>
            </p>
            <button
              type="button"
              onClick={handleChangeNumber}
              className="mt-2 text-xs font-semibold uppercase tracking-[0.12em] text-ink-700 underline decoration-saffron-500 decoration-2 underline-offset-4"
            >
              Change number
            </button>
          </div>

          <div>
            <label
              htmlFor="phone-otp"
              className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700"
            >
              Verification code
            </label>
            <input
              id="phone-otp"
              name="otp"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={6}
              required
              className="w-full bg-white px-4 py-3 text-center font-display text-2xl tracking-[0.35em] text-ink-900 placeholder-ink-600/40 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
              placeholder="••••••"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            />
          </div>

          <button
            type="submit"
            disabled={loading || otp.length !== 6}
            className="group flex w-full items-center justify-center gap-2 bg-saffron-500 px-6 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? (
              'Verifying…'
            ) : (
              <>
                {mode === 'signup' ? 'Create account' : 'Log in'}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </>
            )}
          </button>

          <p className="text-center text-sm text-ink-700/80">
            Didn&apos;t get a code?{' '}
            {resendIn > 0 ? (
              <span>Resend in {resendIn}s</span>
            ) : (
              <button
                type="button"
                onClick={handleResend}
                disabled={loading}
                className="font-semibold text-ink-900 underline decoration-saffron-500 decoration-2 underline-offset-4"
              >
                Resend code
              </button>
            )}
          </p>
        </form>
      )}
    </div>
  );
};

export default PhoneAuthPanel;
